import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import type { ChatSessionSummary } from '../../services/api/chatSessionService';
import {
  addAccountThunk,
  addAccountWithUserData,
  completeAccountSwitchWithUserData,
  processAddAccountThunk,
  switchAccountThunk,
} from '../thunks/multiAccountThunks';

/**
 * Spec B (chat-session-history), Phase 10 — Redux state for the chat-session
 * drawer. Holds the list of session headers, the currently active session id,
 * the drawer open/closed flag, and basic paging metadata.
 *
 * The actual conversation messages still live in `chatSlice.messages` — when a
 * session is loaded, `chatSlice.setMessages` swaps in the persisted messages
 * and `chatSessionSlice.activeSessionId` is updated.
 */
interface ChatSessionState {
  sessions: ChatSessionSummary[];
  activeSessionId: number | null;
  isDrawerOpen: boolean;
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  error: string | null;
}

const initialState: ChatSessionState = {
  sessions: [],
  activeSessionId: null,
  isDrawerOpen: false,
  isLoading: false,
  pageNumber: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
  error: null,
};

function applyAccountReset(state: ChatSessionState) {
  state.sessions = [];
  state.activeSessionId = null;
  state.isDrawerOpen = false;
  state.isLoading = false;
  state.pageNumber = 1;
  state.totalCount = 0;
  state.totalPages = 0;
  state.error = null;
}

const chatSessionSlice = createSlice({
  name: 'chatSession',
  initialState,
  reducers: {
    setDrawerOpen(state, action: PayloadAction<boolean>) {
      state.isDrawerOpen = action.payload;
    },
    toggleDrawer(state) {
      state.isDrawerOpen = !state.isDrawerOpen;
    },
    setActiveSession(state, action: PayloadAction<number | null>) {
      state.activeSessionId = action.payload;
    },
    setSessions(
      state,
      action: PayloadAction<{
        items: ChatSessionSummary[];
        pageNumber: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
      }>
    ) {
      const { items, pageNumber, pageSize, totalCount, totalPages } = action.payload;
      state.sessions = items;
      state.pageNumber = pageNumber;
      state.pageSize = pageSize;
      state.totalCount = totalCount;
      state.totalPages = totalPages;
      state.isLoading = false;
      state.error = null;
    },
    /**
     * Insert-or-replace a single session row in the list. Used by the
     * SSE meta event when a brand-new chat creates a session id the
     * frontend hasn't loaded yet — we insert a stub row at the top so
     * the drawer reflects the new conversation immediately, even if the
     * background `loadSessions` refetch hasn't returned.
     */
    upsertSession(state, action: PayloadAction<ChatSessionSummary>) {
      const incoming = action.payload;
      const idx = state.sessions.findIndex(s => s.id === incoming.id);
      if (idx >= 0) {
        state.sessions[idx] = incoming;
      } else {
        state.sessions.unshift(incoming);
        state.totalCount += 1;
      }
    },
    removeSession(state, action: PayloadAction<number>) {
      const id = action.payload;
      const before = state.sessions.length;
      state.sessions = state.sessions.filter(s => s.id !== id);
      if (state.sessions.length !== before) {
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
      if (state.activeSessionId === id) {
        state.activeSessionId = null;
      }
    },
    updateSessionTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const target = state.sessions.find(s => s.id === action.payload.id);
      if (target) target.title = action.payload.title;
    },
    updateSessionPinned(state, action: PayloadAction<{ id: number; pinned: boolean }>) {
      const target = state.sessions.find(s => s.id === action.payload.id);
      if (target) target.pinned = action.payload.pinned;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    /**
     * Full reset for an account-identity change. Mirrors the chatSlice
     * pattern — see AuthProvider's effect that watches activeAccountId.
     */
    resetForAccountChange(state) {
      applyAccountReset(state);
    },
    /**
     * Reset when the user switches active tenant within the same account.
     * The drawer is scoped to the active tenant on every load, so the cached
     * list is stale after a switch — wipe it and let `loadSessions()` refetch.
     * Intentionally identical to `resetForAccountChange` for now; kept as a
     * separate action so the AuthProvider effect can be unambiguous about
     * which trigger fired it.
     */
    resetForTenantChange(state) {
      applyAccountReset(state);
    },
  },
  extraReducers: (builder) => {
    // Defensive matcher: if any of these account thunks fire, also reset.
    // The primary mechanism is the AuthProvider effect watching activeAccountId.
    builder.addMatcher(
      isAnyOf(
        switchAccountThunk.fulfilled,
        addAccountThunk.fulfilled,
        addAccountWithUserData.fulfilled,
        processAddAccountThunk.fulfilled,
        completeAccountSwitchWithUserData.fulfilled,
      ),
      (state) => {
        applyAccountReset(state);
      }
    );
  },
});

export const {
  setDrawerOpen,
  toggleDrawer,
  setActiveSession,
  setSessions,
  upsertSession,
  removeSession,
  updateSessionTitle,
  updateSessionPinned,
  setLoading,
  setError,
  resetForAccountChange,
  resetForTenantChange,
} = chatSessionSlice.actions;

export default chatSessionSlice.reducer;
